/**
 * @fileoverview Shopify product import page.
 * Allows importing products from Shopify stores with DIRECT connection mode.
 * @module features/shopify-import/pages/ShopifyImportPage
 */

import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Search,
  Store,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Kbd } from '@/components/ui/kbd'
import { Input } from '@/components/ui/input'

import { ScrollReveal } from '@/shared/components/ui'
import {
  ProductImportTable,
  type ImportProductItem,
  type ImportVariantItem,
} from '@/shared/components/ProductImportTable'
import { AddFromBaselinkerSelectionActionBar } from '@/features/baselinker-import/components/AddFromBaselinkerSelectionActionBar'
import {
  useShopifyStoresQuery,
  useShopifyImportPreviewQuery,
  useShopifyImportProductsMutation,
} from '../api/shopifyImportService'
import type {
  ShopifyImportResponse,
  ShopifyProductPreviewItem,
  ShopifyVariantPreviewItem,
} from '../schemas/shopifyImportSchema'

/**
 * Converts a Shopify variant to the generic ImportVariantItem format.
 */
function toImportVariant(variant: ShopifyVariantPreviewItem): ImportVariantItem {
  return {
    variantId: variant.variantId,
    name: variant.title,
    sku: variant.sku,
    imageUrl: variant.imageUrl ?? null,
    alreadyExists: variant.alreadyExists,
    cost: variant.cost ?? null,
    costCurrency: variant.costCurrency ?? null,
  }
}

/**
 * Converts a Shopify product to the generic ImportProductItem format.
 */
function toImportProduct(product: ShopifyProductPreviewItem): ImportProductItem {
  return {
    externalProductId: product.shopifyProductId,
    productName: product.productName,
    imageUrl: product.imageUrl ?? null,
    sku: null, // Shopify doesn't have product-level SKU
    skuAlreadyExists: false,
    allVariantsExist: product.allVariantsExist,
    hasOnlyDefaultVariant: product.hasOnlyDefaultVariant ?? false,
    variantCount: product.variantCount,
    variants: product.variants?.map(toImportVariant) ?? null,
    averageCost: null, // Shopify has cost per variant, not averaged
    siblingConflict: product.siblingConflict ?? false,
    siblingConflictDetails: product.siblingConflictDetails ?? null,
  }
}

export default function ShopifyImportPage(): ReactElement {
  const { t } = useTranslation('features-products')
  const navigate = useNavigate()

  // State
  const [userSelectedConnectionId, setUserSelectedConnectionId] = useState<string | null>(null)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(new Set())
  const [importResult, setImportResult] = useState<ShopifyImportResponse | null>(null)
  const [step, setStep] = useState<number>(0)

  // Queries
  const storesQuery = useShopifyStoresQuery(true)

  // Auto-select first store when there's only one (derived state pattern)
  const stores = storesQuery.data ?? []
  const hasOnlyOneStore = stores.length === 1
  const effectiveConnectionId = userSelectedConnectionId ?? (hasOnlyOneStore ? stores[0]?.connectionId ?? null : null)
  const effectiveStep = effectiveConnectionId && step === 0 ? 1 : step

  const previewQuery = useShopifyImportPreviewQuery(effectiveConnectionId, effectiveConnectionId !== null)
  const importMutation = useShopifyImportProductsMutation()

  // Esc to go back (only when no selection active)
  const hasAnySelection = selectedProductIds.size > 0 || selectedVariantIds.size > 0
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !hasAnySelection) {
        event.preventDefault()
        navigate('/dashboard/management/products')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate, hasAnySelection])

  const hasNoStores = !storesQuery.isLoading && (storesQuery.data?.length ?? 0) === 0

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-screen-lg mx-auto px-4 lg:px-8 pt-24 pb-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/management/products')}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{t('shopifyImport.title', 'Add from Shopify')}</h1>
              <p className="text-muted-foreground">{t('shopifyImport.subtitle', 'Add products and variants from your Shopify store')}</p>
            </div>
          </div>
        </ScrollReveal>

        <div className="flex flex-col gap-8 pb-8">
          {/* No stores message */}
          {hasNoStores && (
            <ScrollReveal delay={0.1}>
              <NoStoresMessage />
            </ScrollReveal>
          )}

          {/* Step 0: Store Selection (shown if multiple stores or loading) */}
          {!hasNoStores && (storesQuery.isLoading || stores.length > 1) && (
            <ScrollReveal delay={0.1}>
              <Step0StoreSelection
                isActive={effectiveStep === 0}
                isCompleted={effectiveStep > 0}
                isLoading={storesQuery.isLoading}
                stores={stores}
                selectedConnectionId={effectiveConnectionId}
                onSelect={(id) => {
                  setUserSelectedConnectionId(id)
                  setStep(1)
                }}
              />
            </ScrollReveal>
          )}

          {/* Step 1: Preview & Select Products */}
          {effectiveStep >= 1 && effectiveConnectionId && (
            <ScrollReveal delay={0.2}>
              <Step1Preview
                isActive={effectiveStep === 1}
                isCompleted={effectiveStep > 1}
                previewQuery={previewQuery}
                selectedProductIds={selectedProductIds}
                selectedVariantIds={selectedVariantIds}
                onProductSelectionChange={setSelectedProductIds}
                onVariantSelectionChange={setSelectedVariantIds}
                isImporting={importMutation.isPending}
                onImport={async () => {
                  try {
                    const result = await importMutation.mutateAsync({
                      connectionId: effectiveConnectionId,
                      request: {
                        productIds: selectedProductIds.size > 0 ? Array.from(selectedProductIds) : undefined,
                      },
                    })
                    setImportResult(result)
                    setStep(2)
                  } catch {
                    toast.error(t('shopifyImport.error', 'Failed to import products'))
                  }
                }}
                onClearSelection={() => {
                  setSelectedProductIds(new Set())
                  setSelectedVariantIds(new Set())
                }}
              />
            </ScrollReveal>
          )}

          {/* Step 2: Summary */}
          {effectiveStep >= 2 && importResult && (
            <ScrollReveal delay={0.3}>
              <Step2Summary
                result={importResult}
                onClose={() => navigate('/dashboard/management/products')}
                onImportMore={() => {
                  setStep(0)
                  setUserSelectedConnectionId(null)
                  setSelectedProductIds(new Set())
                  setSelectedVariantIds(new Set())
                  setImportResult(null)
                }}
              />
            </ScrollReveal>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Subcomponents ---

function NoStoresMessage(): ReactElement {
  const { t } = useTranslation('features-products')
  const navigate = useNavigate()

  return (
    <section className="flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6">
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <Store className="size-12 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{t('shopifyImport.noStores', 'No Shopify stores available')}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('shopifyImport.noStoresDescription', 'Connect a Shopify store in DIRECT mode to import products.')}
          </p>
        </div>
        <Button onClick={() => navigate('/dashboard/settings/integrations')}>
          {t('shopifyImport.goToSettings', 'Go to Integrations')}
        </Button>
      </div>
    </section>
  )
}

interface StoreOption {
  connectionId: string
  name: string
  domain: string
}

function Step0StoreSelection({
  isActive,
  isCompleted,
  isLoading,
  stores,
  selectedConnectionId,
  onSelect,
}: {
  isActive: boolean
  isCompleted: boolean
  isLoading: boolean
  stores: StoreOption[]
  selectedConnectionId: string | null
  onSelect: (id: string) => void
}): ReactElement {
  const { t } = useTranslation('features-products')

  return (
    <section className={`flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300 ${isActive ? 'ring-2 ring-primary/20' : isCompleted ? '' : 'opacity-60'}`}>
      <div className="mb-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'}`}>
            {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '1'}
          </div>
          {t('shopifyImport.steps.store', 'Select Store')}
        </h3>
      </div>
      <div className="ml-10">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-64" />
          </div>
        ) : (
          <Select
            value={selectedConnectionId ?? ''}
            onValueChange={onSelect}
            disabled={!isActive && !isCompleted}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder={t('shopifyImport.selectStore', 'Select a store')} />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.connectionId} value={store.connectionId}>
                  {store.name} ({store.domain})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </section>
  )
}

function Step1Preview({
  isActive,
  isCompleted,
  previewQuery,
  selectedProductIds,
  selectedVariantIds,
  onProductSelectionChange,
  onVariantSelectionChange,
  isImporting,
  onImport,
  onClearSelection,
}: {
  isActive: boolean
  isCompleted: boolean
  previewQuery: ReturnType<typeof useShopifyImportPreviewQuery>
  selectedProductIds: Set<string>
  selectedVariantIds: Set<string>
  onProductSelectionChange: (ids: Set<string>) => void
  onVariantSelectionChange: (ids: Set<string>) => void
  isImporting: boolean
  onImport: () => Promise<void>
  onClearSelection: () => void
}): ReactElement {
  const { t } = useTranslation('features-products')
  const [searchTerm, setSearchTerm] = useState('')

  const allProducts = useMemo(() => {
    if (!previewQuery.data) return []
    return [
      ...previewQuery.data.newProducts,
      ...previewQuery.data.alreadyImportedProducts,
    ].map(toImportProduct)
  }, [previewQuery.data])

  const handleSelectAllNew = useCallback((): void => {
    const newProductIds = new Set<string>()

    allProducts.forEach(p => {
      if (!p.allVariantsExist && !p.siblingConflict) {
        newProductIds.add(p.externalProductId)
      }
    })

    onProductSelectionChange(newProductIds)
    onVariantSelectionChange(new Set())
  }, [allProducts, onProductSelectionChange, onVariantSelectionChange])

  return (
    <section className={`flex flex-col rounded-3xl border border-border/15 bg-card pb-6 pt-6 transition-all duration-300 ${isActive ? 'ring-2 ring-primary/20' : 'opacity-60'}`}>
      <div className="mb-6 grid grid-cols-[1fr_2fr_1fr] items-stretch gap-4 px-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'}`}>
              {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '2'}
            </div>
            {t('shopifyImport.steps.preview', 'Select Products')}
          </h3>
          <p className="text-sm text-muted-foreground ml-10">{t('shopifyImport.steps.previewDesc', 'Choose products to import')}</p>
        </div>
        <div className="relative w-full max-w-md flex items-center">
          <Search className="absolute left-2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('import.searchPlaceholder', 'Search products...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-full focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleSelectAllNew}
            disabled={!isActive}
            className="h-full"
          >
            {t('import.actions.selectAllNew', 'Select all new')}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-h-[400px]">
        <ProductImportTable
          products={allProducts}
          selectedProductIds={selectedProductIds}
          selectedVariantIds={selectedVariantIds}
          onProductSelectionChange={onProductSelectionChange}
          onVariantSelectionChange={onVariantSelectionChange}
          isActive={isActive}
          isLoading={previewQuery.isLoading}
          isError={previewQuery.isError}
          showStatusColumn
          showExistingToggle
          searchTerm={searchTerm}
          noBorder
          maxHeight="none"
          className="flex-1"
          sourceName="Shopify"
          getVariantIdentifier={(v) => v.variantId}
          {...(previewQuery.data?.totalProducts !== undefined && { totalProducts: previewQuery.data.totalProducts })}
          {...(previewQuery.data?.alreadyImported !== undefined && { alreadyImported: previewQuery.data.alreadyImported })}
        />
      </div>

      <AddFromBaselinkerSelectionActionBar
        productCount={selectedProductIds.size}
        variantCount={selectedVariantIds.size}
        isImporting={isImporting}
        onClearSelection={onClearSelection}
        onImport={onImport}
      />
    </section>
  )
}

function Step2Summary({
  result,
  onClose,
  onImportMore,
}: {
  result: ShopifyImportResponse
  onClose: () => void
  onImportMore: () => void
}): ReactElement {
  const { t } = useTranslation('features-products')

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return (
    <section className="flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 ring-2 ring-positive/20">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full text-positive text-sm font-bold">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          {t('import.steps.summary', 'Import Complete')}
        </h3>
        <p className="text-sm text-muted-foreground ml-10">{t('import.steps.summaryDesc', 'Import results summary')}</p>
      </div>
      <div className="space-y-6 ml-10">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-muted/50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-primary">{result.newlyImported}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('import.summary.imported')}</div>
          </div>
          <div className="bg-muted/50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-muted-foreground">{result.alreadyImported}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('import.summary.skipped')}</div>
          </div>
        </div>

        {result.failed > 0 && (
          <div className="bg-destructive/10 p-4 rounded-lg border border-destructive/20">
            <div className="flex items-center gap-2 text-destructive font-medium mb-2">
              <AlertCircle className="h-4 w-4" />
              {t('import.summary.failed', { count: result.failed })}
            </div>
            <ul className="text-sm text-destructive/80 list-disc list-inside">
              {result.errors.slice(0, 3).map((err, i) => (
                <li key={i}>{err.productName}: {err.errorMessage}</li>
              ))}
              {result.errors.length > 3 && (
                <li>...and {result.errors.length - 3} more</li>
              )}
            </ul>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-6 ml-10">
        <Button variant="ghost" onClick={onImportMore}>
          {t('import.actions.importMore', 'Import More')}
        </Button>
        <Button onClick={onClose}>
          {t('import.actions.done', 'Done')}
          <kbd className="ml-2 pointer-events-none h-5 select-none items-center gap-1 rounded border border-border/40 bg-muted px-1.5 font-mono text-[10px] font-medium opacity-60">
            Enter
          </kbd>
        </Button>
      </div>
    </section>
  )
}
